import './Registration.sass';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  capitalize,
  getRandomInt,
  getRegistrationToken,
  getStorageItem,
  statReachGoal,
} from '$utils';
import { Button, Form, FormItem, Header, Input, SegmentedControl } from '$uikit';
import { useTranslator } from '$hooks';
import { IcDice28 } from '$assets';
import { toastActions } from '$store/toast';
import { USER_NAME_REGEX } from '$shared/constants';
import { authActions } from '$store/auth';
import { Sex } from '$store/models';

const FirstNames = [
  'aqua',
  'black',
  'blue',
  'brown',
  'gold',
  'gray',
  'green',
  'lime',
  'orange',
  'pink',
  'purple',
  'red',
  'silver',
  'violet',
  'white',
  'yellow',
];

const LastNames = [
  'albatross',
  'alligator',
  'anaconda',
  'angelfish',
  'ant',
  'antelope',
  'ape',
  'aphid',
  'badger',
  'barracuda',
  'basilisk',
  'bass',
  'bat',
  'bear',
  'beaver',
  'bedbug',
  'bee',
  'beetle',
  'bird',
  'bison',
  'blackbird',
  'boa',
  'boar',
  'bobcat',
  'bug',
  'butterfly',
  'buzzard',
  'camel',
  'capybara',
  'cardinal',
  'caribou',
  'carp',
  'cat',
  'catfish',
  'cattle',
  'centipede',
  'cheetah',
  'chickadee',
  'chicken',
  'chimpanzee',
  'chinchilla',
  'chipmunk',
  'cicada',
  'clam',
  'clownfish',
  'cobra',
  'condor',
  'coral',
  'cougar',
  'cow',
  'coyote',
  'crab',
  'crane',
  'crawdad',
  'crayfish',
  'cricket',
  'crocodile',
  'crow',
  'damselfly',
  'deer',
  'dingo',
  'dinosaur',
  'dog',
  'dolphin',
  'donkey',
  'dormouse',
  'dove',
  'dragon',
  'dragonfly',
  'duck',
  'eagle',
  'earthworm',
  'earwig',
  'echidna',
  'elephant',
  'falcon',
  'finch',
  'firefly',
  'fish',
  'flamingo',
  'flea',
  'fly',
  'fox',
  'frog',
  'gazelle',
  'gecko',
  'gerbil',
  'gibbon',
  'giraffe',
  'goat',
  'goldfish',
  'goose',
  'gopher',
  'gorilla',
  'grouse',
  'gull',
  'guppy',
  'haddock',
  'halibut',
  'hamster',
  'hare',
  'harrier',
  'hawk',
  'hedgehog',
  'heron',
  'herring',
  'hookworm',
  'hornet',
  'horse',
  'hyena',
  'iguana',
  'impala',
  'jackal',
  'jaguar',
  'jay',
  'jellyfish',
  'kangaroo',
  'kingfisher',
  'kite',
  'kiwi',
  'koala',
  'koi',
  'krill',
  'ladybug',
  'lamprey',
  'lark',
  'leech',
  'lemming',
  'lemur',
  'leopard',
  'limpet',
  'lion',
  'lizard',
  'llama',
  'lobster',
  'locust',
  'loon',
  'louse',
  'lungfish',
  'lynx',
  'macaw',
  'mackerel',
  'magpie',
  'mammal',
  'manatee',
  'mandrill',
  'marlin',
  'marmot',
  'marten',
  'mastodon',
  'mink',
  'minnow',
  'mite',
  'mole',
  'mollusk',
  'mongoose',
  'monkey',
  'moose',
  'mosquito',
  'moth',
  'mouse',
  'mule',
  'narwhal',
  'ocelot',
  'octopus',
  'opossum',
  'orangutan',
  'orca',
  'ostrich',
  'otter',
  'owl',
  'ox',
  'panda',
  'panther',
  'parakeet',
  'parrot',
  'partridge',
  'peacock',
  'peafowl',
  'pelican',
  'penguin',
  'perch',
  'pheasant',
  'pig',
  'pigeon',
  'pike',
  'piranha',
  'platypus',
  'pony',
  'porcupine',
  'porpoise',
  'possum',
  'prawn',
  'primate',
  'ptarmigan',
  'puffin',
  'puma',
  'python',
  'quail',
  'rabbit',
  'raccoon',
  'rat',
  'raven',
  'reindeer',
  'reptile',
  'rhinoceros',
  'roadrunner',
  'rodent',
  'rook',
  'rooster',
  'roundworm',
  'sailfish',
  'salamander',
  'salmon',
  'scallop',
  'scorpion',
  'seahorse',
  'shark',
  'sheep',
  'shrew',
  'shrimp',
  'silkworm',
  'skunk',
  'sloth',
  'slug',
  'smelt',
  'snail',
  'snake',
  'snipe',
  'sole',
  'sparrow',
  'spider',
  'spoonbill',
  'squid',
  'squirrel',
  'starfish',
  'stingray',
  'stoat',
  'stork',
  'sturgeon',
  'swallow',
  'swan',
  'swift',
  'swordfish',
  'tapir',
  'tarantula',
  'termite',
  'tern',
  'thrush',
  'tick',
  'tiger',
  'toad',
  'tortoise',
  'toucan',
  'trout',
  'tuna',
  'turkey',
  'turtle',
  'unicorn',
  'vicuna',
  'viper',
  'vole',
  'vulture',
  'wallaby',
  'walrus',
  'warbler',
  'wasp',
  'weasel',
  'whale',
  'wildcat',
  'wolf',
  'wombat',
  'worm',
  'wren',
  'yak',
  'zebra',
];

export function Registration() {
  const t = useTranslator();
  const dispatch = useDispatch();

  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [sex, setSex] = useState<Sex>(Sex.UNKNOWN);
  const [isFillFired, setFillFired] = useState(false);

  useEffect(() => {
    if (!getRegistrationToken()) {
      location.href = '/';
    } else {
      statReachGoal('register_page');
      const tgName = getStorageItem('telegramUserName');
      if (tgName) {
        setName(tgName);
      }

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 1000);
    }
  }, []);

  const handleName = useCallback(
    ({ target }) => {
      setName(target.value);
      if (!isFillFired) {
        statReachGoal('registration_fill_name');
        setFillFired(true);
      }
    },
    [isFillFired],
  );

  const handleRand = useCallback(() => {
    const firstName = capitalize(FirstNames[getRandomInt(0, FirstNames.length - 1)]);
    const lastName = capitalize(LastNames[getRandomInt(0, LastNames.length - 1)]);
    setName(`${firstName} ${lastName}`);
    statReachGoal('registration_generate_name');
  }, []);

  const handleSend = useCallback(() => {
    const nameTrimmed = name.trim();
    if (nameTrimmed.length < 2) {
      return dispatch(toastActions.setToastFail(t('reg_name_short')));
    }

    const exp = nameTrimmed.split(' ');
    const firstName = exp[0];
    if (firstName.trim().length > 16) {
      return dispatch(toastActions.setToastFail(t('reg_name_long')));
    }

    if (!firstName.trim().match(USER_NAME_REGEX)) {
      return dispatch(toastActions.setToastFail(t('reg_name_wrong')));
    }

    const lastName = exp[1] || '';
    if (lastName.length > 0) {
      if (lastName.length > 16) {
        return dispatch(toastActions.setToastFail(t('reg_last_name_long')));
      }

      if (!lastName.match(USER_NAME_REGEX)) {
        return dispatch(toastActions.setToastFail(t('reg_last_name_wrong')));
      }
    }

    if (sex !== Sex.FEMALE && sex !== Sex.MALE) {
      return dispatch(toastActions.setToastFail(t('reg_select_gender')));
    }

    dispatch(
      authActions.createAccount({
        firstName,
        lastName,
        sex,
      }),
    );
  }, [dispatch, name, sex, t]);

  const handleBack = useCallback(() => {
    dispatch(authActions.logout());
    statReachGoal('registration_back');
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <>
      <Header showBackButton backButtonOnClick={handleBack}></Header>
      <div className="Registration__header">
        <div className="Registration__title">{t('registration_title')}</div>
        <div className="Registration__caption">{t('registration_caption')}</div>
      </div>
      <Form>
        <FormItem description=" ">
          <div className="Registration__input_wrap">
            <Input
              ref={inputRef}
              placeholder={t('registration_name_placeholder')}
              onChange={handleName}
              value={name}
              onKeyDown={handleKeyDown}
            />
            <div className="Registration__rand" onClick={handleRand}>
              <IcDice28 />
            </div>
          </div>
        </FormItem>
        <FormItem title={t('reg_gender')}>
          <SegmentedControl
            options={[
              { label: t('reg_gender_female'), value: Sex.FEMALE },
              { label: t('reg_gender_male'), value: Sex.MALE },
            ]}
            defaultValue={sex}
            onChange={setSex}
          />
        </FormItem>
        <FormItem>
          <Button onClick={handleSend}>{t('continue')}</Button>
        </FormItem>
      </Form>
    </>
  );
}
